import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #desktopHoverQuery = window.matchMedia('(hover: hover)');
  #desktopThumbnailQuery = window.matchMedia('(min-width: 750px)');
  #hoverTimer;
  #thumbnailRowsExpanded = false;
  #thumbnailRowsObserver;

  static HOVER_DWELL_MS = 90;
  static VISIBLE_THUMBNAIL_ROWS = 2;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#setupThumbnailHoverPreview();
      this.#setupThumbnailRowToggle();
    });

    this.refs.slideshow?.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearHoverTimer();
    this.#thumbnailRowsObserver?.disconnect();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #setupThumbnailHoverPreview() {
    if (!this.#desktopHoverQuery.matches) return;

    const { slideshow } = this.refs;
    const { thumbnails } = slideshow?.refs || {};
    const { signal } = this.#controller;

    if (!slideshow || !thumbnails?.length) return;

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener(
        'pointerenter',
        () => {
          if (!this.#desktopHoverQuery.matches) return;
          this.#clearHoverTimer();
          this.#hoverTimer = window.setTimeout(() => {
            this.#hoverTimer = undefined;
            if (!this.#desktopHoverQuery.matches) return;
            if (!thumbnail.matches(':hover')) return;

            slideshow.hoverSelect(index);
          }, MediaGallery.HOVER_DWELL_MS);
        },
        { signal }
      );

      thumbnail.addEventListener(
        'pointerleave',
        () => {
          this.#clearHoverTimer();
        },
        { signal }
      );
    });
  }

  #setupThumbnailRowToggle() {
    const elements = this.#getDesktopThumbnailElements();
    if (!elements) return;

    const refresh = () => this.#refreshThumbnailRowToggle();

    this.#thumbnailRowsObserver?.disconnect();
    this.#thumbnailRowsObserver = new ResizeObserver(() => refresh());
    this.#thumbnailRowsObserver.observe(elements.controls);
    this.#thumbnailRowsObserver.observe(elements.thumbnailsList);

    requestAnimationFrame(refresh);
  }

  #handleSlideshowSelect = () => {
    this.#refreshThumbnailRowToggle();
  };

  #clearHoverTimer() {
    if (this.#hoverTimer == null) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = undefined;
  }

  #getDesktopThumbnailElements() {
    const controls = this.querySelector('.media-gallery__desktop-controls[thumbnails]');
    const thumbnailsBlock = controls?.querySelector('.slideshow-controls__thumbnails-block');
    const thumbnailsViewport = controls?.querySelector('.slideshow-controls__thumbnails-viewport');
    const thumbnailsList = controls?.querySelector('.slideshow-controls__thumbnails');
    const toggleButton = controls?.querySelector('.slideshow-controls__expand-toggle');

    if (
      !(controls instanceof HTMLElement) ||
      !(thumbnailsBlock instanceof HTMLElement) ||
      !(thumbnailsViewport instanceof HTMLElement) ||
      !(thumbnailsList instanceof HTMLElement) ||
      !(toggleButton instanceof HTMLButtonElement)
    ) {
      return null;
    }

    const thumbnails = Array.from(thumbnailsList.querySelectorAll('.slideshow-controls__thumbnail')).filter(
      (thumbnail) => thumbnail instanceof HTMLElement
    );

    if (!thumbnails.length) return null;

    return {
      controls,
      thumbnailsBlock,
      thumbnailsViewport,
      thumbnailsList,
      toggleButton,
      thumbnails,
    };
  }

  #refreshThumbnailRowToggle() {
    const elements = this.#getDesktopThumbnailElements();
    if (!elements) return;

    const { thumbnailsBlock, thumbnailsViewport, thumbnailsList, toggleButton, thumbnails } = elements;

    if (!this.#desktopThumbnailQuery.matches) {
      this.#thumbnailRowsExpanded = false;
      thumbnailsBlock.removeAttribute('data-thumbnail-rows');
      thumbnailsBlock.removeAttribute('thumbnail-rows-state');
      thumbnailsBlock.style.removeProperty('--pdp-thumbnail-collapsed-height');
      thumbnailsBlock.style.removeProperty('--pdp-thumbnail-expanded-height');
      thumbnailsViewport.style.removeProperty('max-height');
      toggleButton.hidden = true;
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-label', 'Show more thumbnails');
      return;
    }

    const gridTop = thumbnailsList.getBoundingClientRect().top;
    const thumbnailRows = thumbnails.map((thumbnail) => {
      const rect = thumbnail.getBoundingClientRect();

      return {
        element: thumbnail,
        top: Math.round(rect.top - gridTop),
        bottom: Math.round(rect.bottom - gridTop),
      };
    });
    const rowTops = [...new Set(thumbnailRows.map(({ top }) => top))];
    const visibleRowCount = MediaGallery.VISIBLE_THUMBNAIL_ROWS;

    if (rowTops.length <= visibleRowCount) {
      this.#thumbnailRowsExpanded = false;
      thumbnailsBlock.removeAttribute('data-thumbnail-rows');
      thumbnailsBlock.removeAttribute('thumbnail-rows-state');
      thumbnailsBlock.style.removeProperty('--pdp-thumbnail-collapsed-height');
      thumbnailsBlock.style.removeProperty('--pdp-thumbnail-expanded-height');
      thumbnailsViewport.style.removeProperty('max-height');
      toggleButton.hidden = true;
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-label', 'Show more thumbnails');
      return;
    }

    const lastVisibleRowTop = rowTops[Math.min(visibleRowCount - 1, rowTops.length - 1)];
    const collapsedHeight = Math.max(
      ...thumbnailRows.filter(({ top }) => top <= lastVisibleRowTop).map(({ bottom }) => bottom)
    );

    const expandedHeight = thumbnailsList.scrollHeight;

    thumbnailsBlock.setAttribute('data-thumbnail-rows', 'collapsible');
    thumbnailsBlock.setAttribute('thumbnail-rows-state', this.#thumbnailRowsExpanded ? 'expanded' : 'collapsed');
    thumbnailsBlock.style.setProperty('--pdp-thumbnail-collapsed-height', `${collapsedHeight}px`);
    thumbnailsBlock.style.setProperty('--pdp-thumbnail-expanded-height', `${expandedHeight}px`);
    thumbnailsViewport.style.maxHeight = `${this.#thumbnailRowsExpanded ? expandedHeight : collapsedHeight}px`;

    toggleButton.hidden = false;
    toggleButton.setAttribute('aria-expanded', `${this.#thumbnailRowsExpanded}`);
    toggleButton.setAttribute(
      'aria-label',
      this.#thumbnailRowsExpanded ? 'Show fewer thumbnails' : 'Show more thumbnails'
    );
  }

  toggleThumbnailRows(event) {
    event?.preventDefault();

    const elements = this.#getDesktopThumbnailElements();
    if (!elements || !this.#desktopThumbnailQuery.matches) return;

    this.#thumbnailRowsExpanded = !this.#thumbnailRowsExpanded;
    this.#refreshThumbnailRowToggle();
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (this.dataset.productMediaZoomDisabled === 'true') {
      event?.preventDefault();
      return;
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    if (this.dataset.productMediaZoomDisabled === 'true') return;

    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
